<script setup lang="ts">
import { h, ref, computed, watch } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { AIR_ONLY, AIR_TYPE } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useBookingQuoteStore } from '@/store/BookingQuoteStore';
import { useModalComposable, useViewComposable } from '@/views/composable';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import { useBookComposable } from './useBookComposable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputMask from 'primevue/inputmask';
import InputNumber from 'primevue/inputnumber';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';
import AirPaxName from './AirPaxName.vue';
import UpdatePassengerDetailModal from '@/components/modal/UpdatePassengerDetailModal.vue';
import ExportExcelFile from '../modal/utils/ExportExcelFile.vue';

const props = defineProps<{
  priorityStatus?: number;
  isManagement: boolean;
}>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const adminSetupStore = useAdminSetupStore();
const bookingQuoteStore = useBookingQuoteStore();
const submitWorksheetStore = useSubmitWorksheetStore();
const data = ref();
const airEditingRows = ref([]);
const expandedRows = ref<any>({});
const editingRows = ref([]);
const currentLanguage = getCurrentLocale();

const { handleOpenErrorMessage, showMinNotification } = useModalComposable();
const { canUpdateAirPassenger, hasEditOccupacyPermission, hasViewOccupacyPermission, hasEditBookPermission, checkPriorityStatus, handleDataExport, renderFileNameExport } = useBookComposable();
const { styleColumnExport } = useViewComposable();

const widthList = [5, 10, 15, 25, 20, 25, 10, 20, 20, 20, 15, 10, 15, 15, 15, 15, 30, 30, 15, 15, 10, 15, 15];
const airColumnStyles = styleColumnExport(widthList);
const airMinimumConfig = computed(() => adminSetupStore.minimumConfig?.airConfig);
const quote = computed(() => bookingQuoteStore.quote);

const submitAirQuote = async (updatePassengers: boolean = false) => {
  try {
    const payload = {};
    payload.qType = quote.value.qType;
    payload.airs = quote.value.airs;
    payload.expires = quote.value.expires;
    payload.linkedQuotes = quote.value.linkedQuotes;
    if (updatePassengers) {
      await submitWorksheetStore.updatePassengers(payload, quote.value.sk);
    } else {
      await submitWorksheetStore.handleUpdateWorkSheet(payload, quote.value.sk);
    }
    toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
  } catch {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

const getFlightIndex = (prices: any, priceId: string) => {
  return prices.findIndex((item: any) => item.id === priceId);
};

const validateMaxNumOfSeat = (numOfAdults: number, numOfChildren: number, totalNumOfAdults: number, totalNumOfChildren: number, maxPassengers: number, optionAdultNo: number, optionChdNo: number) => {
  if (numOfAdults > optionAdultNo || numOfChildren > optionChdNo || (maxPassengers && numOfAdults + numOfChildren > maxPassengers) || totalNumOfAdults > optionAdultNo || totalNumOfChildren > optionChdNo) {
    return false;
  }

  return true;
};

const onRowEditSave = ({ newData }, priceItems, dataCommon) => {
  let optionAdt = 0;
  let optionChd = 0;

  if (newData.type == 'Economy') {
    optionAdt = dataCommon?.data?.adtNo ?? 0;
    optionChd = dataCommon?.data?.chdNo ?? 0;
  } else {
    optionAdt = dataCommon?.data?.businessAdtNo ?? 0;
    optionChd = dataCommon?.data?.businessChdNo ?? 0;
  }

  if (newData.adtNo > optionAdt || newData.chdNo > optionChd) {
    return handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_seat'));
  }

  const maxPassengers = newData.numberOfPassengers;
  let totalAdults = newData.adtNo;
  let totalChildren = newData.chdNo;
  (priceItems as any[]).forEach((priceItem) => {
    if (newData.id !== priceItem.id && newData.type === priceItem.type) {
      totalAdults += priceItem.adtNo;
      totalChildren += priceItem.chdNo;
    }
  });

  if (!validateMaxNumOfSeat(newData.adtNo, newData.chdNo, totalAdults, totalChildren, maxPassengers, optionAdt, optionChd)) {
    handleOpenErrorMessage(t('my_quote.toastMessage.over_selected_seat'));
  } else {
    const indexData = getFlightIndex(priceItems, newData.id);
    priceItems[indexData] = { ...newData };
    submitAirQuote();
  }
};

const handleOncloseModal = (data: any, priceItem: any) => {
  document.getElementsByTagName('body')[0].classList.add('p-overflow-hidden-2');
  if (data) {
    priceItem.parties = data.data.parties.map((item) => {
      return {
        adtPassengers: item.adtPassengers,
        chdPassengers: item.chdPassengers,
        email: item.email
      };
    });
    submitAirQuote(true);
  }
};

const handleRemoveKey = (data: any) => {
  if (data) {
    delete data.data.key;
    data.data.parties.forEach((party: any) => {
      delete party.key;
      party.adtPassengers.forEach((adtPassenger: any) => {
        delete adtPassenger.key;
      });
      party.chdPassengers.forEach((chdPassenger: any) => {
        delete chdPassenger.key;
      });
    });
  }
};

const addPax = (priceItem, data) => {
  const getDepDate = () => {
    return data.flights.find((i) => i.type === 'Inbound')?.depDate;
  };

  const dialogRef = dialog.open(AirPaxName, {
    data: {
      quoteId: quote.value.sk,
      priorityStatus: props.priorityStatus,
      data: priceItem,
      qType: AIR_ONLY,
      gType: quote.value.gType,
      isManagement: props.isManagement,
      retDate: getDepDate()
    },
    props: {
      modal: true,
      maximizable: true,
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      style: {
        width: '95vw'
      }
    },
    templates: {
      header: () => {
        return [h('div', { class: 'flex align-items-center' }, [t('booking.hint_header'), h('i', { class: 'text-4xl ml-3 fa-hand-point-right fa-solid' })])];
      },
      footer: () => {
        if (hasEditOccupacyPermission) {
          return [
            h(Button, {
              class: 'mt-4 add-pax',
              label: t('common.save'),
              icon: 'pi pi-check',
              disabled: !(priceItem.adtNo + priceItem.chdNo),
              autofocus: true,
              onClick: () => dialogRef.close({ buttonType: 'Save', data: dialogRef.data.occupancy })
            })
          ];
        }
      }
    },
    onClose: ({ data }) => {
      handleRemoveKey(data);
      handleOncloseModal(data, priceItem);
    }
  });
};

const validateTotalNumOfSeats = (totalNumberOfSeats: number, totalOptionPax: number) => {
  return totalNumberOfSeats <= totalOptionPax;
};

const handleIsSaveEmit = (dialogRef: any, value: any, dataIndex: number) => {
  let totalEconomyAdultsSeats = 0;
  let totalEconomyChildrenSeats = 0;
  let totalBusinessAdultsSeats = 0;
  let totalBusinessChildrenSeats = 0;

  if (!quote.value.isReplicate && canUpdateAirPassenger(data.value, dataIndex, value, airMinimumConfig.value)) {
    showMinNotification(t('advance_quote.form.min_pax_air_book', { minPax: airMinimumConfig.value.minPax }));
    return;
  }
  data.value[dataIndex].price.forEach((priceItem: any) => {
    if (priceItem.type === 'Economy') {
      totalEconomyAdultsSeats += priceItem.adtNo;
      totalEconomyChildrenSeats += priceItem.chdNo;
    } else {
      totalBusinessAdultsSeats += priceItem.adtNo;
      totalBusinessChildrenSeats += priceItem.chdNo;
    }
  });

  dialogRef.close();
  data.value[dataIndex].adtNo = value.adtNo;
  data.value[dataIndex].chdNo = value.chdNo;
  data.value[dataIndex].businessAdtNo = value.businessAdtNo;
  data.value[dataIndex].businessChdNo = value.businessChdNo;

  if (!validateTotalNumOfSeats(totalEconomyAdultsSeats, value.adtNo) || !validateTotalNumOfSeats(totalEconomyChildrenSeats, value.chdNo)) {
    for (const index in data.value[dataIndex].price) {
      if (data.value[dataIndex].price[index].type === 'Economy') {
        data.value[dataIndex].price[index].adtNo = 0;
        data.value[dataIndex].price[index].chdNo = 0;
      }
    }
  }

  if (!validateTotalNumOfSeats(totalBusinessAdultsSeats, value.businessAdtNo) || !validateTotalNumOfSeats(totalBusinessChildrenSeats, value.businessChdNo)) {
    for (const index in data.value[dataIndex].price) {
      if (data.value[dataIndex].price[index].type === 'Business') {
        data.value[dataIndex].price[index].adtNo = 0;
        data.value[dataIndex].price[index].chdNo = 0;
      }
    }
  }

  submitAirQuote();
};

const openEditModal = (detailData, dataIndex) => {
  const dialogRef = dialog.open(UpdatePassengerDetailModal, {
    props: {
      header: t('book_form.updates_passenger_details'),
      modal: true,
      style: {
        'min-width': '50vw'
      }
    },
    data: {
      passengerData: detailData
    },
    emits: {
      isSave: (value) => {
        handleIsSaveEmit(dialogRef, value, dataIndex);
      }
    }
  });
};

const formatBookingId = (bookingIds: string) => {
  if (bookingIds) {
    return bookingIds.replace(/,/g, '\n');
  }
};

const handleShowInfo = (data: any, type: string) => {
  if (type === 'Adult' && !props.isManagement) {
    return `${t('history_log.air_economy_adult')}: ${data.adtNo}\n${t('history_log.air_business_adult')}: ${data.businessAdtNo}`;
  }
  if (type === 'Child' && !props.isManagement) {
    return `${t('history_log.air_economy_children')}: ${data.chdNo}\n${t('history_log.air_business_children')}: ${data.businessChdNo}`;
  }
};

const handleHeaderLead = () => {
  const gType = quote.value.gType;
  return gType.toUpperCase() === 'WEDDING' ? t('book_form.bride_groom') : t('book_form.group_leader');
};

watch(
  quote,
  (newQuote) => {
    if (newQuote) {
      data.value = newQuote.airs;
      data.value?.forEach((air, airIndex) => {
        air.key = `Air${airIndex}`;
        air.price.forEach((p, priceIndex) => {
          p.id = `${airIndex}${priceIndex}`;
        });
        expandedRows.value = { ...expandedRows.value, [air.key]: true };
      });
    }
  },
  { immediate: true }
);
</script>
<template>
  <div class="booking-option-wrap">
    <DataTable :value="data" v-model:expandedRows="expandedRows" dataKey="key" editMode="row" v-model:editingRows="airEditingRows">
      <template #header>
        <div class="grid">
          <div class="col">
            <h5>
              {{ $t('book_form.occupancy') }}
              <br />
              <small>{{ $t('book_form.quote_no') }}.: {{ quote.sk }}</small>
              <br />
              <small class="pre-line">{{ $t('book_form.booking_no') }}.: {{ formatBookingId(quote.bookingId) }}</small>
            </h5>
          </div>
          <div class="col">
            <p>
              {{ $t('advance_quote.form.group_name') }}: {{ quote.gName }}
              <br />
              {{ $t('advance_quote.form.group_type') }}: {{ quote.gType || '-' }}
              <br />
              {{ $t('advance_quote.form.group_contact') }}: {{ quote.gContact }}
            </p>
            <div class="align-items-center hidden">
              <!-- TODO - 20240610 - to be removed? -->
              <label for="emergencyPh">{{ $t('book_form.emergency_phone') }}</label>
              <div class="col">
                <InputMask id="emergencyPh" :mask="$t('common.labels.emergency_phone_mask')" v-model="quote.agencyPhone" :placeholder="$t('common.labels.emergency_phone_number')" />
              </div>
            </div>
          </div>
          <div class="col-2 flex justify-content-end align-items-end">
            <ExportExcelFile v-slot="slotProps" :columnsStyle="airColumnStyles" :fileName="renderFileNameExport(quote.sk, quote?.gName)" :isAoaToSheet="true">
              <Button type="button" icon="pi pi-external-link" :label="$t('common.buttons.export_pax_list')" class="bg-primary export-pax-btn" @click="handleDataExport(slotProps.export, data, isManagement, handleHeaderLead(), AIR_TYPE, 'price')" />
            </ExportExcelFile>
          </div>
        </div>
      </template>
      <TableColumn :expander="true" class="w-3rem" />
      <TableColumn field="option" :header="$t('book_form.option')">
        <template #body="slotProps"> {{ $t('book_form.option') }} {{ slotProps.index + 1 }} </template>
      </TableColumn>
      <TableColumn field="itinerary" :header="$t('worksheet.type')">
        <template #body="{ data }">
          {{ $t(`advance_quote.form.${data.itinerary.toLowerCase()}`) }}
        </template>
      </TableColumn>
      <TableColumn field="gate" :header="$t('book_form.from')"> </TableColumn>
      <TableColumn field="dest" :header="$t('book_form.to')"> </TableColumn>
      <TableColumn field="depDate" :header="$t('book_form.departure')">
        <template #body="{ data }">
          {{ formatDate(data.depDate, 'dd-LLL-yyyy', true, currentLanguage) }}
        </template>
      </TableColumn>
      <TableColumn field="adtNo" :header="$t('book_form.adults')">
        <template #body="{ data }">
          <span v-tooltip.top="handleShowInfo(data, 'Adult')">
            {{ (parseInt(data.adtNo, 10) ?? 0) + (parseInt(data.businessAdtNo, 10) ?? 0) }}
          </span>
        </template>
      </TableColumn>
      <TableColumn field="chdNo" :header="$t('book_form.children')">
        <template #body="{ data }">
          <span v-tooltip.top="handleShowInfo(data, 'Child')">
            {{ (parseInt(data.chdNo, 10) ?? 0) + (parseInt(data.businessChdNo, 10) ?? 0) }}
          </span>
        </template>
      </TableColumn>
      <TableColumn v-if="isManagement" :header="$t('common.edit')" class="py-3">
        <template #body="{ data, index }">
          <i class="pi pi-pencil cursor-pointer" @click="openEditModal(data, index)"></i>
        </template>
      </TableColumn>

      <template #expansion="slotProps">
        <DataTable
          :value="slotProps.data.price"
          data-key="id"
          :sortOrder="0"
          editMode="row"
          v-model:editingRows="editingRows"
          @row-edit-save="onRowEditSave($event, slotProps.data.price, slotProps)"
          class="editable-cells-table bg-blue-50"
          responsiveLayout="stack"
        >
          <TableColumn field="type" :header="$t('book_form.air_class')" class="py-3">
            <template #body="{ data }">
              {{ data.type }}
              <template v-if="data.numberOfPassengers">
                <span>
                  - {{ (data.fareBasis?.split(' / ') ?? []).map((part: string) => part.charAt(0)).join('/') }}
                  <span class="font-italic">
                    ({{
                      $t('booking.maximum_count_pax', {
                        count: data.numberOfPassengers
                      })
                    }})
                  </span>
                </span>
              </template>
              <span v-else> - N/A</span>
            </template>
          </TableColumn>
          <TableColumn field="adtNo" :header="$t('book_form.adults')">
            <template #body="{ data, field }">
              {{ data[field] || 0 }}
            </template>
            <template #editor="{ data, field }">
              <InputNumber :min="0" :max="1000" v-model="data[field]" autofocus />
            </template>
          </TableColumn>
          <TableColumn field="chdNo" :header="$t('book_form.children')">
            <template #body="{ data, field }">
              {{ data[field] || 0 }}
            </template>
            <template #editor="{ data, field }">
              <InputNumber :min="0" :max="1000" v-model="data[field]" autofocus />
            </template>
          </TableColumn>
          <TableColumn v-if="hasEditBookPermission && !checkPriorityStatus(priorityStatus, isManagement)" :header="$t('common.edit')" :rowEditor="true" class="py-3 min-w-8rem">
            <template #body="{ data, editorInitCallback }">
              <Button :disabled="!data.numberOfPassengers" icon="pi pi-pencil" class="p-button-secondary p-datatable-row-editor-init" @click="editorInitCallback($event)" />
            </template>
            <template #editor="{ data, editorSaveCallback, editorCancelCallback }">
              <Button :disabled="!data.numberOfPassengers" icon="pi pi-check" class="p-button-secondary p-datatable-row-editor-save" @click="editorSaveCallback($event)" />
              <Button :disabled="!data.numberOfPassengers" icon="pi pi-times" class="p-button-secondary p-datatable-row-editor-cancel" @click="editorCancelCallback($event)" />
            </template>
          </TableColumn>
          <TableColumn v-if="hasViewOccupacyPermission" :header="$t('common.action')" class="py-3">
            <template #body="{ data }">
              <Button :label="isManagement ? $t('book_form.edit_pax') : $t('book_form.add_pax')" @click="addPax(data, slotProps.data)" class="p-button-outlined" />
            </template>
          </TableColumn>
        </DataTable>
      </template>
    </DataTable>
  </div>
</template>
<style lang="scss">
.p-overflow-hidden-2 {
  overflow: hidden;
}
.min-w-8rem {
  min-width: 8rem;
}
.pre-line {
  white-space: pre-line;
}
</style>
